// Product Cost Structure - Linking Products to Real Suppliers
import { suppliers } from './suppliers';
import { products } from './data';

// Real Cost Breakdown for Each Product
export const productCosts = {
  // === CANDLES ===
  'prod-007': { // Ocean Breeze Candle
    name: 'Ocean Breeze Candle',
    supplierBreakdown: {
      candle: {
        supplierId: 'sup-candle-001', // AliExpress
        item: 'Soy wax jar candle 200g',
        cost: 2.50,
        url: 'https://www.aliexpress.com/item/soy-candles',
        notes: 'Bulk order 50pcs'
      },
      fragrance: {
        supplierId: 'sup-frag-001', // Amazon
        item: 'Ocean breeze fragrance oil 100ml',
        cost: 3.20,
        url: 'https://www.amazon.de/dp/ocean-scent',
        notes: '~15ml per candle'
      },
      packaging: {
        supplierId: 'sup-pack-001', // AliExpress
        item: 'Luxury gift box with ribbon',
        cost: 1.50,
        url: 'https://www.aliexpress.com/item/luxury-box',
        notes: 'Premium quality box'
      },
      label: {
        supplierId: 'sup-pack-003', // Etikett.de
        item: 'Custom label (round 5cm)',
        cost: 0.40,
        url: 'https://www.etikett.de/custom-labels',
        notes: 'Minimum order 250pcs'
      }
    },
    additionalCosts: {
      labor: 4.00, // Your time for assembly
      overhead: 0.80, // Utilities, storage, etc.
      shipping: 2.50  // DHL to customer
    },
    get totalCost() {
      const supplierCosts = Object.values(this.supplierBreakdown)
        .reduce((sum, item) => sum + item.cost, 0);
      const additionalTotal = Object.values(this.additionalCosts)
        .reduce((sum, cost) => sum + cost, 0);
      return supplierCosts + additionalTotal;
    },
    targetMargin: 0.40, // 40% profit margin
    get suggestedPrice() {
      return (this.totalCost / (1 - this.targetMargin)).toFixed(2);
    },
    currentRetailPrice: 25.00
  },

  'prod-008': { // Sandalwood & Vanilla Candle
    name: 'Sandalwood & Vanilla Candle',
    supplierBreakdown: {
      candle: {
        supplierId: 'sup-candle-001',
        item: 'Soy wax jar candle 200g',
        cost: 2.50,
        url: 'https://www.aliexpress.com/item/soy-candles',
        notes: 'Same as Ocean Breeze'
      },
      fragranceSandalwood: {
        supplierId: 'sup-frag-001',
        item: 'Sandalwood essential oil',
        cost: 2.80,
        url: 'https://www.amazon.de/s?k=sandalwood+oil',
        notes: '10ml per candle'
      },
      fragranceVanilla: {
        supplierId: 'sup-frag-001',
        item: 'Vanilla fragrance oil',
        cost: 2.00,
        url: 'https://www.amazon.de/s?k=vanilla+oil',
        notes: '5ml per candle'
      },
      packaging: {
        supplierId: 'sup-pack-001',
        item: 'Premium gift box',
        cost: 1.80,
        url: 'https://www.aliexpress.com/item/luxury-box',
        notes: 'Slightly larger box'
      },
      label: {
        supplierId: 'sup-pack-003',
        item: 'Custom label',
        cost: 0.40,
        url: 'https://www.etikett.de/custom-labels'
      }
    },
    additionalCosts: {
      labor: 4.50,
      overhead: 0.90,
      shipping: 2.50
    },
    get totalCost() {
      const supplierCosts = Object.values(this.supplierBreakdown)
        .reduce((sum, item) => sum + item.cost, 0);
      const additionalTotal = Object.values(this.additionalCosts)
        .reduce((sum, cost) => sum + cost, 0);
      return supplierCosts + additionalTotal;
    },
    targetMargin: 0.42,
    get suggestedPrice() {
      return (this.totalCost / (1 - this.targetMargin)).toFixed(2);
    },
    currentRetailPrice: 28.00
  },

  // === SOAPS ===
  'prod-003': { // Lavender Dream Soap
    name: 'Lavender Dream Soap',
    supplierBreakdown: {
      soapBase: {
        supplierId: 'sup-candle-002', // Local Munich supplier
        item: 'Glycerin soap base 1kg',
        cost: 1.20,
        url: 'https://www.kerzen-herold.de/soap-base',
        notes: '~100g per soap'
      },
      lavenderOil: {
        supplierId: 'sup-frag-002', // Dragonspice
        item: 'Lavender essential oil',
        cost: 1.50,
        url: 'https://www.dragonspice.de/lavender-oil',
        notes: 'Certified organic'
      },
      packaging: {
        supplierId: 'sup-pack-001',
        item: 'Kraft paper box',
        cost: 0.60,
        url: 'https://www.aliexpress.com/item/kraft-box',
        notes: 'Eco-friendly packaging'
      },
      label: {
        supplierId: 'sup-pack-003',
        item: 'Soap label sticker',
        cost: 0.30,
        url: 'https://www.etikett.de/soap-labels'
      }
    },
    additionalCosts: {
      labor: 2.50,
      overhead: 0.40,
      shipping: 1.80
    },
    get totalCost() {
      const supplierCosts = Object.values(this.supplierBreakdown)
        .reduce((sum, item) => sum + item.cost, 0);
      const additionalTotal = Object.values(this.additionalCosts)
        .reduce((sum, cost) => sum + cost, 0);
      return supplierCosts + additionalTotal;
    },
    targetMargin: 0.50, // 50% margin on soaps
    get suggestedPrice() {
      return (this.totalCost / (1 - this.targetMargin)).toFixed(2);
    },
    currentRetailPrice: 12.00
  },

  // === FLOWERS ===
  'prod-001': { // Pastel Rose Bouquet
    name: 'Pastel Rose Bouquet',
    supplierBreakdown: {
      roses: {
        supplierId: 'sup-flower-001', // Blumen Risse
        item: 'Pastel roses (20 stems)',
        cost: 35.00,
        url: 'https://www.blumen-risse.de/roses',
        notes: 'Seasonal pricing varies'
      },
      greens: {
        supplierId: 'sup-flower-002', // Tollwood market
        item: 'Filler greens and baby breath',
        cost: 8.00,
        url: 'https://www.tollwood.de',
        notes: 'Market purchase'
      },
      wrapping: {
        supplierId: 'sup-pack-002', // Packari
        item: 'Eco wrapping paper + ribbon',
        cost: 3.50,
        url: 'https://www.packari.com/gift-wrap',
        notes: 'Sustainable packaging'
      }
    },
    additionalCosts: {
      labor: 15.00, // Arranging takes time!
      overhead: 2.00,
      shipping: 8.50 // Special flower delivery
    },
    get totalCost() {
      const supplierCosts = Object.values(this.supplierBreakdown)
        .reduce((sum, item) => sum + item.cost, 0);
      const additionalTotal = Object.values(this.additionalCosts)
        .reduce((sum, cost) => sum + cost, 0);
      return supplierCosts + additionalTotal;
    },
    targetMargin: 0.35, // 35% margin on fresh flowers
    get suggestedPrice() {
      return (this.totalCost / (1 - this.targetMargin)).toFixed(2);
    },
    currentRetailPrice: 75.00
  },

  // === DECORATIONS ===
  'prod-005': { // Gilded Agate Coasters
    name: 'Gilded Agate Coasters',
    supplierBreakdown: {
      coasters: {
        supplierId: 'sup-pack-001', // AliExpress
        item: 'Agate coaster set (4pcs)',
        cost: 8.50,
        url: 'https://www.aliexpress.com/item/agate-coasters',
        notes: 'Natural stone coasters'
      },
      goldLeaf: {
        supplierId: 'sup-decor-001', // Deko-Fabrik
        item: 'Gold leaf foil kit',
        cost: 3.00,
        url: 'https://www.deko-fabrik-muenchen.de/gold-leaf',
        notes: 'DIY gold leafing'
      },
      packaging: {
        supplierId: 'sup-pack-001',
        item: 'Gift box with foam insert',
        cost: 2.50,
        url: 'https://www.aliexpress.com/item/coaster-box'
      }
    },
    additionalCosts: {
      labor: 8.00, // Hand-applying gold leaf
      overhead: 1.50,
      shipping: 3.50
    },
    get totalCost() {
      const supplierCosts = Object.values(this.supplierBreakdown)
        .reduce((sum, item) => sum + item.cost, 0);
      const additionalTotal = Object.values(this.additionalCosts)
        .reduce((sum, cost) => sum + cost, 0);
      return supplierCosts + additionalTotal;
    },
    targetMargin: 0.45,
    get suggestedPrice() {
      return (this.totalCost / (1 - this.targetMargin)).toFixed(2);
    },
    currentRetailPrice: 45.00
  }
};

// Helper function to get product cost info
export function getProductCostInfo(productId) {
  const costInfo = productCosts[productId];
  if (!costInfo) {
    return {
      available: false,
      message: 'Cost information not available for this product'
    };
  }

  const totalCost = costInfo.totalCost;
  const suggestedPrice = parseFloat(costInfo.suggestedPrice);
  const currentPrice = costInfo.currentRetailPrice;
  const actualMargin = ((currentPrice - totalCost) / currentPrice * 100).toFixed(1);

  return {
    available: true,
    productName: costInfo.name,
    breakdown: costInfo.supplierBreakdown,
    additionalCosts: costInfo.additionalCosts,
    totalCost: totalCost.toFixed(2),
    suggestedPrice: suggestedPrice.toFixed(2),
    currentPrice: currentPrice.toFixed(2),
    targetMargin: (costInfo.targetMargin * 100).toFixed(0) + '%',
    actualMargin: actualMargin + '%',
    profit: (currentPrice - totalCost).toFixed(2),
    needsPriceAdjustment: Math.abs(currentPrice - suggestedPrice) > 2
  };
}

// Calculate if you're making good margins
export function analyzeAllProductMargins() {
  const analysis = [];

  for (const [productId, costInfo] of Object.entries(productCosts)) {
    const info = getProductCostInfo(productId);
    if (info.available) {
      analysis.push({
        productId,
        name: info.productName,
        currentPrice: parseFloat(info.currentPrice),
        suggestedPrice: parseFloat(info.suggestedPrice),
        profit: parseFloat(info.profit),
        margin: info.actualMargin,
        status: info.needsPriceAdjustment ? 'Review Pricing' : 'Good',
        needsReview: info.needsPriceAdjustment
      });
    }
  }

  return analysis.sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
}

// Supplier spending summary
export function getSupplierSpendingSummary() {
  const spending = {};

  for (const costInfo of Object.values(productCosts)) {
    for (const [key, item] of Object.entries(costInfo.supplierBreakdown)) {
      const supplierId = item.supplierId;
      if (!spending[supplierId]) {
        const supplier = suppliers.find(s => s.id === supplierId);
        spending[supplierId] = {
          supplierName: supplier?.name || 'Unknown',
          totalSpending: 0,
          items: []
        };
      }
      spending[supplierId].totalSpending += item.cost;
      spending[supplierId].items.push({
        product: costInfo.name,
        item: item.item,
        cost: item.cost
      });
    }
  }

  return spending;
}
